import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storyBibleApi } from '../../api/storyBible';
import Loading from '../Common/Loading';
import { useApp } from '../../context/AppContext';
import { bringIntoBookPath } from '../../utils/bringIntoBookPath';
import { styleCardsPath } from '../../utils/styleCardsPath';
import { readLastProjectId, readLastStoryId } from '../../utils/lastProject';
import { BRING_TITLE } from '../../pages/bringIntoBookCopy';
import CardDetailPanel from './CardDetailPanel';
import NamingWizard from './NamingWizard';
import SamePersonMerge from './SamePersonMerge';
import StartCharacterFocusDialog from './StartCharacterFocusDialog';
import { formatPersonLabel, parsePersonLabel } from '../../utils/personNames';
import {
  TIER1_CATEGORIES, TIER2_CATEGORIES, bucketEntries, groupByInstance, isMultiInstanceCategory,
  bookNoteCategoryDomId, categoryDisplayName, latestSingletonEntry,
} from './categories';
import { findBookCard, isThemeOrLoglineKind, leftoverKindToCategory } from '../../utils/bookCards';
import { BACK_TO_TASKS, clearTasksReturn, readTasksReturn } from '../../utils/handoffReturn';
import { isSeriesThemeContext } from '../../utils/bringIntoBook';
import BookStructureDoor from '../Frameworks/BookStructureDoor';
import { publishChatFocus } from '../Chat/ChatPaneFrame';
import { publishChatAttachment } from '../Chat/chatAttachment';
import {
  ADD_CUSTOM_NOTE,
  BRAINSTORM_IN_CHAT,
  MAKE_A_NOTE,
  NOTE_EMPTY,
  NOTE_FILTER_LABEL,
  NOTES_FILTER_ALL,
  NOTES_ROOM_TITLE,
  NO_NOTES_SWITCH_GRID,
  LOADING_BOOK_NOTES,
  SELECT_STORY_FOR_BOOK_NOTES,
  SERIES_NOTES_BOOK_TIP,
  STYLE_FILTER_BODY,
  STYLE_NOTES,
  TRY_AGAIN,
  noKindNotesYet,
} from '../../utils/writerCopy';
import { savedOnLabel } from '../../utils/noteHistory';
import { bindNoteRefresh } from '../../utils/noteChanged';
import PendingOffers from './PendingOffers';
import BookToolsGroup from '../BookTools/BookToolsGroup';
import './BookSheet.css';

function SavedOn({ at }) {
  const label = savedOnLabel(at);
  if (!label) return null;
  return <span className="bs-saved-on">{label}</span>;
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

/**
 * /bibles has no book id in the address. The open book is restored
 * after first paint. Until that book or its notes arrive, the room
 * uses the shared loading state instead of an empty sheet.
 */
function notesStillOpening({ storyId, bible, projectsLoaded, projectOpenError }) {
  if (storyId || bible || projectOpenError) return false;
  if (projectsLoaded === false) return true;
  return Boolean(readLastProjectId() || readLastStoryId());
}

function NotesLoading() {
  return (
    <div className="bs-panel" role="status" aria-busy="true">
      <Loading text={LOADING_BOOK_NOTES} />
    </div>
  );
}

/**
 * BookSheet — Book Notes primary entry point (book-sheet-cards-spec.md
 * v1.2, Increment 1). Replaces StoryBibleEditor as the Book Notes surface
 * for both standalone projects and a series book's Notes room
 * (StoryBibleEditor.jsx itself is untouched, just unlinked here — deleting
 * it is a later, separate cleanup per the spec's own Increment Plan).
 *
 * Master-detail, mirroring SeriesSheet.jsx's tile-grid + portal-mounted
 * detail panel pattern: tiles are fixed, non-interactive previews; every
 * edit/generate/approve/discard action lives in CardDetailPanel.
 *
 * Data shape note (critical): POST .../bible/add-cards always files new
 * entries into a single "Unsorted" section — Book Notes do NOT group
 * by section the way the old free-form editor did. This component fetches
 * the full nested bible (unchanged endpoint) and re-buckets client-side by
 * the `category`/`tier`/`instance_name` fields (migration 143), ignoring
 * section structure entirely — see categories.js.
 */
function BookSheet({
  leftoverKind = '',
  leftoverName = '',
  leftoverWriteup = false,
  focusCategory = '',
} = {}) {
  const { currentStory, currentProject, projectsLoaded, projectOpenError } = useApp();
  const navigate = useNavigate();
  const storyId = currentStory?.id;
  const showBring = Boolean(currentProject?.is_series && storyId);

  const [bible, setBible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadAbortRef = useRef(null);
  const onScreenStoryRef = useRef(null);
  const [boundStoryId, setBoundStoryId] = useState(storyId || null);
  // A missing id for one frame is not a different book. Clearing the
  // sheet here is what replaced Notes with a full-page "Loading…".
  if (storyId && boundStoryId && storyId !== boundStoryId) {
    setBoundStoryId(storyId);
    onScreenStoryRef.current = null;
    setBible(null);
    setLoading(true);
    setError(null);
  } else if (storyId && !boundStoryId) {
    setBoundStoryId(storyId);
  }
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [noteFilter, setNoteFilter] = useState('all');
  const [tier2Expanded, setTier2Expanded] = useState(false);

  // Unsaved, client-only draft cards — created by the naming wizard or a
  // "+ add" control, before any Generate/Approve has given them a
  // description. add-cards still skips an empty description unless the
  // card carries a series_snapshot_id (Bring into this book thin accept).
  // These drafts never touch the backend until the writer fills one in
  // and approves from the detail panel. Lost on refresh by design.
  const [pendingDrafts, setPendingDrafts] = useState([]);
  // null, or the category the detect-and-pick wizard is currently open for
  // ('Character' or 'Setting/Location' — the two categories with a live
  // detection endpoint; Relationship/Subplot use the plain name-only add
  // form below instead, per the Increment 2 design resolution).
  const [wizardCategory, setWizardCategory] = useState(null);
  const [mergingPeople, setMergingPeople] = useState(false);
  const [showCharacterFocusStart, setShowCharacterFocusStart] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [customInstanceName, setCustomInstanceName] = useState('');
  const [addingTier2For, setAddingTier2For] = useState(null); // category or null
  const [tier2NameDraft, setTier2NameDraft] = useState('');

  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [selectedDraftKey, setSelectedDraftKey] = useState(null);
  const [openWriteup, setOpenWriteup] = useState(false);
  const [focusEditor, setFocusEditor] = useState(false);
  const leftoverApplied = useRef('');

  const loadBible = useCallback(async () => {
    if (!storyId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    const keepOnScreen = onScreenStoryRef.current
      && String(onScreenStoryRef.current) === String(storyId);
    try {
      if (!keepOnScreen) setLoading(true);
      const data = await storyBibleApi.get(storyId, { signal: controller.signal });
      if (controller.signal.aborted) return;
      onScreenStoryRef.current = storyId;
      setBible(data);
      setError(null);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      // No bible row yet — still show the default empty tiles, same as
      // Series Notes' seeded core slots. Generate/approve creates the row.
      if (e.status === 404) {
        onScreenStoryRef.current = storyId;
        setBible({ sections: [] });
        setError(null);
      } else {
        setError(e.message);
        if (!keepOnScreen) setBible({ sections: [] });
      }
    } finally {
      if (loadAbortRef.current === controller) setLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    loadBible();
    return () => { loadAbortRef.current?.abort(); };
  }, [loadBible]);

  useEffect(() => {
    if (!storyId) return undefined;
    return bindNoteRefresh(storyId, () => { void loadBible(); });
  }, [storyId, loadBible]);

  const focusMatch = useMemo(() => {
    const wanted = String(focusCategory || '').trim().toLowerCase();
    if (!wanted) return '';
    return [...TIER1_CATEGORIES, ...TIER2_CATEGORIES].find(
      (category) => category.toLowerCase() === wanted,
    ) || '';
  }, [focusCategory]);

  useEffect(() => {
    if (!focusMatch) return;
    if (TIER2_CATEGORIES.includes(focusMatch)) setTier2Expanded(true);
    setViewMode('grid');
  }, [focusMatch]);

  useEffect(() => {
    if (!focusMatch || loading) return;
    if (TIER2_CATEGORIES.includes(focusMatch) && !tier2Expanded) return;
    if (viewMode !== 'grid') return;
    const el = document.getElementById(bookNoteCategoryDomId(focusMatch));
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [focusMatch, loading, tier2Expanded, viewMode]);

  const clearedForStoryRef = useRef(storyId || null);
  useEffect(() => {
    // A one-frame gap is the same book. Only a real change drops drafts.
    if (!storyId || clearedForStoryRef.current === storyId) return;
    clearedForStoryRef.current = storyId;
    setPendingDrafts([]);
    setSelectedEntryId(null);
    setSelectedDraftKey(null);
    setOpenWriteup(false);
    setFocusEditor(false);
    leftoverApplied.current = '';
  }, [storyId]);

  const { tier1, tier2, other, custom } = useMemo(() => bucketEntries(bible), [bible]);

  // Increment 2, Relationship "Parties" nice-to-have: confirmed Character
  // names, for CardDetailPanel to offer as click-to-insert reference chips
  // (spec: "autocomplete against confirmed Character notes ... if it fits
  // without adding real scope" — see CardDetailPanel for the scope call).
  const confirmedCharacterNames = useMemo(
    () => (tier1['Character'] || [])
      .filter((e) => e.confirmed && (e.instance_name || e.name))
      .map((e) => e.instance_name || e.name),
    [tier1]
  );

  const characterMergeItems = useMemo(() => {
    const items = [];
    for (const e of (tier1.Character || [])) {
      const parsed = parsePersonLabel(e.instance_name || e.name || '');
      items.push({
        id: e.id,
        label: formatPersonLabel(e.instance_name || e.name, parsed.nickname),
        primaryName: parsed.name,
        nickname: parsed.nickname,
        alsoKnownAs: [parsed.name, parsed.nickname].filter(Boolean),
        kind: 'card',
        entry: e,
      });
    }
    for (const d of pendingDrafts.filter((row) => row.category === 'Character')) {
      items.push({
        id: d.key,
        label: formatPersonLabel(d.instanceName, d.nickname),
        primaryName: d.instanceName,
        nickname: d.nickname || '',
        alsoKnownAs: d.alsoKnownAs || [],
        kind: 'draft',
        draft: d,
      });
    }
    return items;
  }, [tier1, pendingDrafts]);

  const handlePeopleMerge = async (result) => {
    const keep = result.keep;
    const absorb = result.absorb;
    const label = result.label;
    try {
      if (keep.kind === 'card' && absorb.kind === 'card') {
        const keepDesc = (keep.entry.description || '').trim();
        const absorbDesc = (absorb.entry.description || '').trim();
        const description = keepDesc && absorbDesc && keepDesc !== absorbDesc
          ? `${keepDesc}\n\n${absorbDesc}`
          : (keepDesc || absorbDesc);
        await storyBibleApi.updateEntry(storyId, keep.entry.id, {
          name: label,
          description: description || keep.entry.description,
          instance_name: label,
        });
        await storyBibleApi.deleteEntry(storyId, absorb.entry.id);
        await loadBible();
      } else if (keep.kind === 'card' || absorb.kind === 'card') {
        const cardItem = keep.kind === 'card' ? keep : absorb;
        const draftItem = keep.kind === 'draft' ? keep : absorb;
        await storyBibleApi.updateEntry(storyId, cardItem.entry.id, {
          name: label,
          description: cardItem.entry.description,
          instance_name: label,
        });
        removeDraft(draftItem.draft.key);
        await loadBible();
      } else {
        setPendingDrafts((prev) => prev
          .filter((d) => d.key !== absorb.draft.key)
          .map((d) => (d.key === keep.draft.key
            ? {
              ...d,
              instanceName: result.name,
              nickname: result.nickname,
              alsoKnownAs: result.alsoKnownAs,
              unnamedFlagged: false,
            }
            : d)));
      }
      if (selectedEntryId === absorb.id || selectedDraftKey === absorb.id) closePanel();
      setMergingPeople(false);
    } catch (e) {
      setError(e.message || 'Could not merge those notes.');
      setMergingPeople(false);
    }
  };

  const characterFocusOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    const add = (id, rawLabel, unnamed) => {
      const label = (rawLabel || '').trim() || (unnamed ? 'no name yet' : '');
      if (!label) return;
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      opts.push({ id, label });
    };
    for (const e of (tier1.Character || [])) {
      add(e.id, e.instance_name || e.name, e.unnamed_flagged);
    }
    for (const d of pendingDrafts.filter((d) => d.category === 'Character')) {
      add(d.key, d.instanceName, d.unnamedFlagged);
    }
    return opts;
  }, [tier1, pendingDrafts]);

  const allEntries = useMemo(
    () => (bible?.sections || []).flatMap((s) => s.entries || []),
    [bible]
  );

  const selectedEntry = useMemo(
    () => (selectedEntryId ? allEntries.find((e) => e.id === selectedEntryId) || null : null),
    [allEntries, selectedEntryId]
  );
  const selectedDraft = useMemo(
    () => (selectedDraftKey ? pendingDrafts.find((d) => d.key === selectedDraftKey) || null : null),
    [pendingDrafts, selectedDraftKey]
  );

  // Selected draft disappeared (approved/discarded elsewhere) or selected
  // entry disappeared (deleted) — close instead of pointing at nothing.
  useEffect(() => {
    if (selectedEntryId && bible && !selectedEntry) setSelectedEntryId(null);
  }, [bible, selectedEntryId, selectedEntry]);
  useEffect(() => {
    if (selectedDraftKey && !selectedDraft) setSelectedDraftKey(null);
  }, [selectedDraftKey, selectedDraft]);

  const openEntry = (entry, { writeup = false } = {}) => {
    setSelectedDraftKey(null);
    setSelectedEntryId(entry.id);
    setOpenWriteup(Boolean(writeup));
  };
  const openDraft = (key) => {
    setSelectedEntryId(null);
    setSelectedDraftKey(key);
    setOpenWriteup(false);
  };
  const closePanel = () => {
    setSelectedEntryId(null);
    setSelectedDraftKey(null);
    setOpenWriteup(false);
    setFocusEditor(false);
  };

  useEffect(() => {
    if (loading || !bible) return;
    if (!leftoverKind && !leftoverName) return;
    const leftoverKey = `${storyId}:${leftoverKind}:${leftoverName}:${leftoverWriteup}`;
    if (leftoverApplied.current === leftoverKey) return;
    leftoverApplied.current = leftoverKey;
    const match = findBookCard(allEntries, { kind: leftoverKind, name: leftoverName });
    if (match) {
      setSelectedDraftKey(null);
      setSelectedEntryId(match.id);
      setOpenWriteup(Boolean(leftoverWriteup));
      setFocusEditor(isThemeOrLoglineKind(leftoverKind));
      return;
    }
    if (!isThemeOrLoglineKind(leftoverKind)) return;
    const category = leftoverKindToCategory(leftoverKind);
    const existing = latestSingletonEntry(tier1[category] || []);
    if (existing) {
      setSelectedDraftKey(null);
      setSelectedEntryId(existing.id);
      setOpenWriteup(false);
      setFocusEditor(true);
      return;
    }
    const key = nextDraftKey();
    setPendingDrafts((prev) => [...prev, {
      category,
      tier: 'tier_1',
      instanceName: null,
      unnamedFlagged: false,
      key,
    }]);
    setSelectedEntryId(null);
    setSelectedDraftKey(key);
    setOpenWriteup(false);
    setFocusEditor(true);
  }, [loading, bible, leftoverKind, leftoverName, leftoverWriteup, allEntries, storyId, tier1]);

  const addDraft = (draft) => {
    const key = nextDraftKey();
    setPendingDrafts((prev) => [...prev, { ...draft, key }]);
    return key;
  };
  const removeDraft = (key) => setPendingDrafts((prev) => prev.filter((d) => d.key !== key));

  const openSingletonCard = (category) => {
    const existing = latestSingletonEntry(tier1[category] || tier2[category] || []);
    if (existing) { openEntry(existing); return; }
    const key = addDraft({ category, tier: TIER1_CATEGORIES.includes(category) ? 'tier_1' : 'tier_2', instanceName: null, unnamedFlagged: false });
    openDraft(key);
  };

  const openWizard = (category) => setWizardCategory(category);
  const handleWizardConfirm = (drafts) => {
    const category = wizardCategory;
    const tier = TIER1_CATEGORIES.includes(category) ? 'tier_1' : 'tier_2';
    setWizardCategory(null);
    let firstKey = null;
    for (const d of drafts) {
      const key = addDraft({
        category,
        tier,
        instanceName: d.instanceName,
        nickname: d.nickname || '',
        alsoKnownAs: d.alsoKnownAs || [],
        unnamedFlagged: d.unnamedFlagged,
      });
      if (!firstKey) firstKey = key;
    }
    if (firstKey) openDraft(firstKey);
  };

  const handleAddTier2Instance = (category) => {
    const name = tier2NameDraft.trim();
    if (!name) return;
    const key = addDraft({ category, tier: 'tier_2', instanceName: name, unnamedFlagged: false });
    setAddingTier2For(null);
    setTier2NameDraft('');
    openDraft(key);
  };

  const handleAddCustom = () => {
    const category = customCategory.trim();
    if (!category) return;
    const key = addDraft({ category, tier: 'custom', instanceName: customInstanceName.trim() || null, unnamedFlagged: false });
    setAddingCustom(false);
    setCustomCategory('');
    setCustomInstanceName('');
    openDraft(key);
  };

  const draftsFor = (category) => pendingDrafts.filter((d) => d.category.toLowerCase() === category.toLowerCase());

  const detailCard = selectedEntry
    ? selectedEntry
    : selectedDraft
      ? {
        category: selectedDraft.category,
        tier: selectedDraft.tier,
        instanceName: selectedDraft.instanceName,
        nickname: selectedDraft.nickname || '',
        alsoKnownAs: selectedDraft.alsoKnownAs || [],
        unnamedFlagged: selectedDraft.unnamedFlagged,
        description: '',
      }
      : null;
  const themeEntry = latestSingletonEntry(tier1.Theme);
  const seriesThemeContext = useMemo(
    () => allEntries.find((e) => isSeriesThemeContext(e.category)) || null,
    [allEntries],
  );

  // ─── Tiles ───────────────────────────────────────────────────────────

  const renderSingletonTile = (category) => {
    const entry = latestSingletonEntry(tier1[category] || tier2[category] || []);
    const active = (entry && selectedEntryId === entry.id) || false;
    return (
      <button
        key={category}
        type="button"
        id={bookNoteCategoryDomId(category)}
        data-book-note-category={category}
        className={`bs-tile ${entry ? '' : 'bs-tile--empty'} ${active ? 'bs-tile--active' : ''} ${focusMatch === category ? 'bs-tile--linked' : ''}`}
        onClick={() => openSingletonCard(category)}
      >
        <div className="bs-tile__header">
          <h3 className="bs-tile__title">{categoryDisplayName(category)}</h3>
        </div>
        {entry ? (
          <>
            <p className="bs-tile__preview">{entry.description}</p>
            <SavedOn at={entry.updated_at || entry.created_at} />
          </>
        ) : (
          <>
            <p className="bs-tile__cta">{NOTE_EMPTY}</p>
            <p className="bs-tile__cta">{MAKE_A_NOTE}</p>
          </>
        )}
      </button>
    );
  };

  const renderEmptySnapshotTile = (category) => (
    <button
      key={category}
      type="button"
      id={bookNoteCategoryDomId(category)}
      data-book-note-category={category}
      className={`bs-tile bs-tile--empty ${focusMatch === category ? 'bs-tile--linked' : ''}`}
      onClick={() => {
        const key = addDraft({
          category,
          tier: TIER1_CATEGORIES.includes(category) ? 'tier_1' : 'tier_2',
          instanceName: null,
          unnamedFlagged: true,
        });
        openDraft(key);
      }}
    >
      <div className="bs-tile__header">
        <h3 className="bs-tile__title">{categoryDisplayName(category)}</h3>
      </div>
      <p className="bs-tile__cta">{NOTE_EMPTY}</p>
      <p className="bs-tile__cta">{MAKE_A_NOTE}</p>
    </button>
  );

  const renderMultiInstanceTile = (category, { onAddNew }) => {
    const entries = (tier1[category] || []).concat(tier2[category] || []).concat(other[category] || []);
    const instances = groupByInstance(entries);
    const drafts = draftsFor(category);

    // People / Character empty tile must match the other snapshot tiles
    // (#663): same line, same copy, same “Make a note.” Do not list names
    // from chat and do not open the detect-and-pick wizard on first click.
    if (category === 'Character' && Object.keys(instances).length === 0 && drafts.length === 0) {
      return renderEmptySnapshotTile(category);
    }

    return (
      <div
        key={category}
        id={bookNoteCategoryDomId(category)}
        data-book-note-category={category}
        className={`bs-tile bs-tile--container ${focusMatch === category ? 'bs-tile--linked' : ''}`}
      >
        <div className="bs-tile__header">
          <h3 className="bs-tile__title">{category}</h3>
        </div>
        {Object.keys(instances).length === 0 && drafts.length === 0 && (
          <p className="bs-tile__cta">{noKindNotesYet(category)}</p>
        )}
        <ul className="bs-tile__instances">
          {Object.entries(instances).map(([instanceName, group]) => {
            const entry = group[0];
            return (
              <li key={instanceName}>
                <button
                  type="button"
                  className={`bs-tile__instance ${selectedEntryId === entry.id ? 'bs-tile__instance--active' : ''}`}
                  onClick={() => openEntry(entry)}
                >
                  {entry.unnamed_flagged && !entry.name ? <em>no name yet</em> : formatPersonLabel(instanceName, parsePersonLabel(entry.name).nickname)}
                  <SavedOn at={entry.updated_at || entry.created_at} />
                </button>
              </li>
            );
          })}
          {drafts.map((d) => (
            <li key={d.key}>
              <button
                type="button"
                className={`bs-tile__instance bs-tile__instance--draft ${selectedDraftKey === d.key ? 'bs-tile__instance--active' : ''}`}
                onClick={() => openDraft(d.key)}
              >
                {d.unnamedFlagged ? <em>{formatPersonLabel(d.instanceName, d.nickname)} (unnamed)</em> : formatPersonLabel(d.instanceName, d.nickname)} <span className="bs-tile__draft-tag">not yet generated</span>
              </button>
            </li>
          ))}
        </ul>
        {onAddNew}
      </div>
    );
  };

  const showCategory = (category) => noteFilter === 'all' || noteFilter === category;
  const tier2HasNotes = (category) => (tier2[category] || []).length > 0;
  const renderTier2Tile = (category) => renderMultiInstanceTile(category, {
    onAddNew: addingTier2For === category ? (
      <div className="bs-tile__add-form">
        <input
          className="bs-edit__input"
          value={tier2NameDraft}
          onChange={(e) => setTier2NameDraft(e.target.value)}
          placeholder={`Name this ${category.toLowerCase()}`}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddTier2Instance(category); if (e.key === 'Escape') setAddingTier2For(null); }}
        />
        <div className="bs-tile__add-form-actions">
          <button type="button" className="bs-btn bs-btn--primary bs-btn--small" onClick={() => handleAddTier2Instance(category)}>Add</button>
          <button type="button" className="bs-btn bs-btn--secondary bs-btn--small" onClick={() => setAddingTier2For(null)}>Cancel</button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        className="bs-add-link"
        onClick={() => (
          category === 'Setting/Location'
            ? openWizard(category)
            : (() => { setAddingTier2For(category); setTier2NameDraft(''); })()
        )}
      >
        {category === 'Setting/Location' ? `+ New ${category}` : `+ Add ${category.toLowerCase()}`}
      </button>
    ),
  });
  const tier2WithNotes = TIER2_CATEGORIES.filter(
    (category) => showCategory(category) && tier2HasNotes(category),
  );
  const tier2StillHidden = TIER2_CATEGORIES.filter(
    (category) => showCategory(category) && !tier2HasNotes(category),
  );
  const showHiddenTier2 = tier2StillHidden.length > 0 && (
    noteFilter === 'all' ? tier2Expanded : true
  );
  const openStyleNotes = () => {
    const id = storyId || boundStoryId;
    if (!id) return;
    navigate(styleCardsPath(id));
  };

  const brainstormOpen = () => {
    if (selectedEntry?.id) {
      const label = selectedEntry.name || categoryDisplayName(selectedEntry.category);
      publishChatAttachment({
        kind: 'note',
        id: selectedEntry.id,
        label,
        storyId,
      });
      publishChatFocus(label, '');
      return;
    }
    publishChatAttachment(null);
    publishChatFocus(NOTES_ROOM_TITLE, '');
  };

  if (!bible && (storyId || notesStillOpening({
    storyId, bible, projectsLoaded, projectOpenError,
  }))) {
    return <NotesLoading />;
  }
  if (!storyId && !bible) {
    return <div className="bs-panel"><p>{SELECT_STORY_FOR_BOOK_NOTES}</p></div>;
  }

  return (
    <div className="bs-panel">
      <div className="bs-header">
        <div className="bs-heading">
          <div className="bs-title-row">
            <h1>{NOTES_ROOM_TITLE}</h1>
            <BookToolsGroup />
          </div>
          <p className="bs-header__subtitle">{currentStory?.name || ''}</p>
          {currentProject?.is_series && (
            <p className="bs-header__tip" data-testid="series-book-tip">{SERIES_NOTES_BOOK_TIP}</p>
          )}
        </div>
        <div className="bs-header__style">
          <button type="button" className="bs-style-notes-entry" onClick={openStyleNotes}>
            {STYLE_NOTES}
          </button>
          <p className="bs-header__style-note">{STYLE_FILTER_BODY}</p>
        </div>
        <div className="bs-filters" role="tablist" aria-label={NOTE_FILTER_LABEL}>
          <button
            type="button"
            role="tab"
            className="bs-filters__tab"
            aria-selected={noteFilter === 'all'}
            onClick={() => setNoteFilter('all')}
          >
            {NOTES_FILTER_ALL}
          </button>
          {[...TIER1_CATEGORIES, ...TIER2_CATEGORIES].map((category) => (
            <button
              key={category}
              type="button"
              role="tab"
              className="bs-filters__tab"
              aria-selected={noteFilter === category}
              onClick={() => setNoteFilter(category)}
            >
              {categoryDisplayName(category)}
            </button>
          ))}
        </div>
        <div className="bs-header__actions">
          <div className="bs-header__actions-row">
          <button type="button" className="bs-add-link" onClick={brainstormOpen}>
            {BRAINSTORM_IN_CHAT}
          </button>
          {isThemeOrLoglineKind(leftoverKind) && readTasksReturn(storyId) ? (
            <button
              type="button"
              className="bs-add-link"
              onClick={() => {
                const returnTo = readTasksReturn(storyId);
                clearTasksReturn(storyId);
                if (returnTo) navigate(returnTo);
              }}
            >
              {BACK_TO_TASKS}
            </button>
          ) : null}
          {showBring && (
            <button
              type="button"
              className="bs-add-link"
              onClick={() => navigate(bringIntoBookPath(storyId))}
            >
              {BRING_TITLE}
            </button>
          )}
          <div className="bs-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`bs-view-toggle__btn ${viewMode === 'grid' ? 'bs-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-pressed={viewMode === 'grid'}
            >
              Grid
            </button>
            <button
              type="button"
              className={`bs-view-toggle__btn ${viewMode === 'list' ? 'bs-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
            >
              List
            </button>
          </div>
          </div>
          <BookStructureDoor
            storyId={storyId}
            triggerClassName="bs-add-link"
          />
        </div>
      </div>

      {error && (
        <div className="bs-error" role="alert">
          {error}{' '}
          <button type="button" className="bs-btn bs-btn--secondary" onClick={() => loadBible()}>{TRY_AGAIN}</button>
        </div>
      )}

      <PendingOffers storyId={storyId} onChanged={loadBible} />

      {viewMode === 'grid' ? (
        <div className="bs-content">
          <div className="bs-grid">
            {TIER1_CATEGORIES.filter(showCategory).map((category) => (
              isMultiInstanceCategory(category)
                ? renderMultiInstanceTile(category, {
                  onAddNew: (
                    <div className="bs-tile__add-row">
                      <button type="button" className="bs-add-link" onClick={() => openWizard(category)}>
                        + New {category}
                      </button>
                      {category === 'Character' && (
                        <>
                        <button
                          type="button"
                          className="bs-add-link"
                          onClick={() => setShowCharacterFocusStart(true)}
                        >
                          Create Character Focus Session
                        </button>
                        {characterMergeItems.length >= 2 && (
                          <button
                            type="button"
                            className="bs-add-link"
                            onClick={() => setMergingPeople(true)}
                          >
                            These are the same person…
                          </button>
                        )}
                        </>
                      )}
                    </div>
                  ),
                })
                : renderSingletonTile(category)
            ))}
          </div>

          {Object.keys(other).filter(showCategory).length > 0 && (
            <div className="bs-grid">
              {Object.keys(other).filter(showCategory).map((category) => (
                renderMultiInstanceTile(category, { onAddNew: null })
              ))}
            </div>
          )}

          {tier2WithNotes.length > 0 && (
            <div className="bs-grid">
              {tier2WithNotes.map((category) => renderTier2Tile(category))}
            </div>
          )}

          {noteFilter === 'all' && (
          <button
            type="button"
            className="bs-more-toggle"
            onClick={() => setTier2Expanded((v) => !v)}
            aria-expanded={tier2Expanded}
          >
            {tier2Expanded ? '− Fewer categories' : '+ See more categories'}
          </button>
          )}

          {showHiddenTier2 && (
            <div className="bs-grid">
              {tier2StillHidden.map((category) => renderTier2Tile(category))}
            </div>
          )}

          <div className="bs-custom-section">
            <h2 className="bs-custom-section__title">Custom</h2>
            {custom.length === 0 && pendingDrafts.filter((d) => d.tier === 'custom').length === 0 && (
              <p className="bs-tile__cta">No custom notes yet.</p>
            )}
            <div className="bs-grid">
              {Object.entries(groupByInstance(custom)).map(([label, group]) => {
                const entry = group[0];
                return (
                  <button
                    key={entry.id}
                    type="button"
                    className={`bs-tile ${selectedEntryId === entry.id ? 'bs-tile--active' : ''}`}
                    onClick={() => openEntry(entry)}
                  >
                    <div className="bs-tile__header">
                      <h3 className="bs-tile__title">{entry.name || label}</h3>
                      <span className="bs-tile__badge">{entry.category}</span>
                    </div>
                    <p className="bs-tile__preview">{entry.description}</p>
                    <SavedOn at={entry.updated_at || entry.created_at} />
                  </button>
                );
              })}
              {pendingDrafts.filter((d) => d.tier === 'custom').map((d) => (
                <button
                  key={d.key}
                  type="button"
                  className={`bs-tile bs-tile--empty ${selectedDraftKey === d.key ? 'bs-tile--active' : ''}`}
                  onClick={() => openDraft(d.key)}
                >
                  <div className="bs-tile__header">
                    <h3 className="bs-tile__title">{d.instanceName || d.category}</h3>
                    <span className="bs-tile__badge">{d.category}</span>
                  </div>
                  <p className="bs-tile__cta">Not yet generated</p>
                </button>
              ))}
              {addingCustom ? (
                <div className="bs-tile bs-tile__add-form">
                  <div className="bs-edit__field">
                    <label className="bs-edit__label" htmlFor="bs-custom-category">Category</label>
                    <input id="bs-custom-category" className="bs-edit__input" value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} placeholder="e.g. Backstory" autoFocus />
                  </div>
                  <div className="bs-edit__field">
                    <label className="bs-edit__label">Instance name (optional)</label>
                    <input className="bs-edit__input" value={customInstanceName} onChange={(e) => setCustomInstanceName(e.target.value)} placeholder="Leave blank for a singleton note" />
                  </div>
                  <div className="bs-tile__add-form-actions">
                    <button type="button" className="bs-btn bs-btn--primary bs-btn--small" onClick={handleAddCustom}>Add</button>
                    <button type="button" className="bs-btn bs-btn--secondary bs-btn--small" onClick={() => setAddingCustom(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="bs-tile bs-tile--empty" onClick={() => setAddingCustom(true)}>
                  <div className="bs-tile__header"><h3 className="bs-tile__title">{ADD_CUSTOM_NOTE}</h3></div>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bs-list">
          {allEntries.filter((e) => (e.category || '').trim()).length === 0 ? (
            <p className="bs-tile__cta">{NO_NOTES_SWITCH_GRID}</p>
          ) : (
            allEntries.filter((e) => (e.category || '').trim() && !isSeriesThemeContext(e.category) && showCategory(e.category)).map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`bs-list-row ${selectedEntryId === entry.id ? 'bs-list-row--active' : ''}`}
                onClick={() => openEntry(entry)}
              >
                <span className="bs-list-row__category">{categoryDisplayName(entry.category)}</span>
                <span className="bs-list-row__name">{formatPersonLabel(entry.instance_name || entry.name, parsePersonLabel(entry.name || entry.instance_name).nickname) || (entry.unnamed_flagged ? 'no name yet' : entry.category)}</span>
                <span className="bs-list-row__preview">
                  {entry.description}
                  <SavedOn at={entry.updated_at || entry.created_at} />
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {detailCard && (
        <CardDetailPanel
          storyId={storyId}
          card={detailCard}
          onClose={closePanel}
          onSaved={loadBible}
          onDiscardDraft={selectedDraft ? () => removeDraft(selectedDraft.key) : undefined}
          characterNames={confirmedCharacterNames}
          themePreview={themeEntry?.description || ''}
          hasTheme={Boolean(themeEntry)}
          wantWriteup={openWriteup}
          focusOnOpen={focusEditor}
          seriesBand={
            (detailCard.category || '').trim().toLowerCase() === 'theme' && !themeEntry
              ? (seriesThemeContext?.series_band || '')
              : (detailCard.series_band || '')
          }
          seriesCiteName={
            (detailCard.category || '').trim().toLowerCase() === 'theme' && !themeEntry
              ? (seriesThemeContext?.name || 'Series Theme')
              : (detailCard.name || '')
          }
        />
      )}

      {wizardCategory && (
        <NamingWizard
          storyId={storyId}
          category={wizardCategory}
          onCancel={() => setWizardCategory(null)}
          onConfirm={handleWizardConfirm}
        />
      )}

      {mergingPeople && (
        <SamePersonMerge
          items={characterMergeItems}
          onCancel={() => setMergingPeople(false)}
          onConfirm={handlePeopleMerge}
        />
      )}

      {showCharacterFocusStart && (
        <StartCharacterFocusDialog
          storyId={storyId}
          existingCharacters={characterFocusOptions}
          onClose={() => setShowCharacterFocusStart(false)}
        />
      )}

      <nav className="bs-moved-rooms" aria-label="More rooms">
        <button
          type="button"
          className="bs-add-link"
          onClick={() => navigate('/bibles', { state: { openReference: 'names' } })}
        >
          Names
        </button>
        <button
          type="button"
          className="bs-add-link"
          onClick={() => navigate('/bibles', { state: { openReference: 'captures' } })}
        >
          Compendium
        </button>
      </nav>
    </div>
  );
}

export default BookSheet;
